/**
 * Training-week arithmetic (W9 rhythm).
 *
 * A "training week" is an ISO 8601 week: Monday-start, identified as
 * `YYYY-Www` (e.g. "2026-W28"). Weekly drill stamps key on this, so the
 * scheduled sweep, the lazy stamp in the launcher, and the dashboard card
 * all agree on which week "now" belongs to without coordination.
 *
 * All math is done on the UTC calendar date. The production sweep fires
 * Monday 05:00 America/Chicago (= Monday 10:00/11:00 UTC), so server and
 * client compute the same key all week; the seam around Sunday-evening CT
 * is self-healing because stamping is idempotent and lazy.
 */
const DAY_MS = 86_400_000;
/** Midnight UTC of the given instant's calendar date. */
function utcDate(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
/**
 * ISO 8601 week key ("2026-W28") for an instant. The ISO year can differ
 * from the calendar year at the boundaries (2027-01-01 → "2026-W53").
 */
export function isoWeekKey(date) {
    const d = utcDate(date);
    const dayOfWeek = d.getUTCDay() || 7; // Mon=1 … Sun=7
    // Shift to this week's Thursday: the ISO year is the Thursday's year.
    d.setUTCDate(d.getUTCDate() + 4 - dayOfWeek);
    const isoYear = d.getUTCFullYear();
    const yearStart = Date.UTC(isoYear, 0, 1);
    const week = Math.ceil(((d.getTime() - yearStart) / DAY_MS + 1) / 7);
    return `${isoYear}-W${String(week).padStart(2, '0')}`;
}
/** Midnight UTC of the Monday starting the instant's ISO week. */
export function isoWeekStart(date) {
    const d = utcDate(date);
    const dayOfWeek = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() - (dayOfWeek - 1));
    return d;
}
/** Week key N weeks after (or before, negative) the given instant's week. */
export function shiftWeekKey(date, weeks) {
    return isoWeekKey(new Date(isoWeekStart(date).getTime() + weeks * 7 * DAY_MS));
}
//# sourceMappingURL=week.js.map